import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, Tabs } from '@/shared/ui';
import type { CalendarTransportFilter } from '../calendarModel';

interface CalendarToolbarProps {
  month: Date;
  transport: CalendarTransportFilter;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
  onTransportChange: (filter: CalendarTransportFilter) => void;
}

/** Horní lišta kalendáře: navigace měsíců, „Dnes“, titulek a filtr typu dopravy. */
export function CalendarToolbar({
  month,
  transport,
  onPrevMonth,
  onNextMonth,
  onToday,
  onTransportChange,
}: CalendarToolbarProps) {
  const { t, i18n } = useTranslation('planner');

  const title = new Intl.DateTimeFormat(i18n.language, {
    month: 'long',
    year: 'numeric',
  }).format(month);

  return (
    <div className="gap-phx-3 flex shrink-0 flex-wrap items-center">
      <div className="gap-phx-2 flex items-center">
        <Button
          size="sm"
          variant="outline"
          onClick={onPrevMonth}
          aria-label={t('calendar.toolbar.prevMonth')}
          data-testid="calendar-prev-month"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onNextMonth}
          aria-label={t('calendar.toolbar.nextMonth')}
          data-testid="calendar-next-month"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="outline" onClick={onToday} data-testid="calendar-today">
          {t('calendar.toolbar.today')}
        </Button>
      </div>
      <h2
        className="text-phx-lg text-phx-text font-semibold capitalize"
        data-testid="calendar-title"
      >
        {title}
      </h2>
      <span className="flex-1" />
      <Tabs<CalendarTransportFilter>
        variant="segmented"
        size="sm"
        items={[
          { value: 'all', label: t('calendar.transportFilter.all') },
          { value: 'air', label: t('calendar.transportFilter.air') },
          { value: 'coach', label: t('calendar.transportFilter.coach') },
        ]}
        value={transport}
        onChange={onTransportChange}
      />
    </div>
  );
}
